import { Component, OnInit, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

interface ColorOption {
  name: string;
  code: string;
  isUnique: boolean;
}

interface Project {
  id: string;
  projectName: string;
  modelYear: string;
  modelCode: string;
  projectCode: string;
  colorCode: string;
  colorName: string;
  namingMode: 'standard' | 'custom';
  dateCreated: string;
  isDeleted: boolean;
}
@Component({
  selector: 'app-add-project-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatIconModule],
  templateUrl: './add-project-dialog.component.html',
  styleUrl: './add-project-dialog.component.css'
})
export class AddProjectDialogComponent implements OnInit {
  projectForm!: FormGroup;
  namingMode: 'standard' | 'custom' = 'standard';
  generatedProjectName = '';
  selectedColor: ColorOption | null = null;
  colorError = false;
  selectedColorProject = '';
  existingProjects: Project[] = [];
  mode: 'create' | 'edit' = 'create';
  private readonly editProjectData?: Project;
  duplicateNameError = '';
  private cachedCustomName: string | null = null;

  colorOptions: ColorOption[] = [
    { name: 'Red', code: '#E53935', isUnique: true },
    { name: 'Pink', code: '#D81B60', isUnique: false },
    { name: 'Purple', code: '#8E24AA', isUnique: false },
    { name: 'Deep Purple', code: '#5E35B1', isUnique: false },
    { name: 'Indigo', code: '#3949AB', isUnique: false },
    { name: 'Blue', code: '#1E88E5', isUnique: false },
    { name: 'Light Blue', code: '#039BE5', isUnique: false },
    { name: 'Cyan', code: '#00ACC1', isUnique: false },
    { name: 'Teal', code: '#00897B', isUnique: false },
    { name: 'Green', code: '#43A047', isUnique: false },
    { name: 'Light Green', code: '#7CB342', isUnique: false },
    { name: 'Lime', code: '#C0CA33', isUnique: false },
    { name: 'Yellow', code: '#FDD835', isUnique: false },
    { name: 'Amber', code: '#FFB300', isUnique: false },
    { name: 'Orange', code: '#FB8C00', isUnique: false },
    { name: 'Deep Orange', code: '#F4511E', isUnique: false },
    { name: 'Brown', code: '#6D4C41', isUnique: false },
    { name: 'Grey', code: '#757575', isUnique: false },
    { name: 'Blue Grey', code: '#546E7A', isUnique: false },
    { name: 'Crimson', code: '#C62828', isUnique: false },
    { name: 'Rose', code: '#EC407A', isUnique: false },
    { name: 'Violet', code: '#AB47BC', isUnique: false },
    { name: 'Lavender', code: '#7E57C2', isUnique: false },
    { name: 'Navy', code: '#283593', isUnique: false },
    { name: 'Sky Blue', code: '#42A5F5', isUnique: false },
    { name: 'Aqua', code: '#26C6DA', isUnique: false },
    { name: 'Turquoise', code: '#26A69A', isUnique: false },
    { name: 'Emerald', code: '#66BB6A', isUnique: false },
    { name: 'Olive', code: '#9E9D24', isUnique: false },
    { name: 'Gold', code: '#F9A825', isUnique: false },
    { name: 'Copper', code: '#EF6C00', isUnique: false },
    { name: 'Rust', code: '#D84315', isUnique: false },
    { name: 'Chocolate', code: '#5D4037', isUnique: false },
    { name: 'Slate', code: '#455A64', isUnique: false },
    { name: 'Charcoal', code: '#424242', isUnique: false },
    { name: 'Black', code: '#212121', isUnique: false }
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialogRef: MatDialogRef<AddProjectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { projects: Project[]; mode?: 'create' | 'edit'; project?: Project }
  ) {
    this.existingProjects = data?.projects || [];
    this.mode = data?.mode || 'create';
    this.editProjectData = data?.project;
    this.initializeForm();
  }

  ngOnInit(): void {
    if (this.mode === 'edit' && this.editProjectData) {
      this.namingMode = this.editProjectData.namingMode;
    }
    this.onNamingModeChange();
    if (this.mode === 'edit' && this.editProjectData) {
      this.populateForEdit(this.editProjectData);
    }

    this.projectForm.get('projectName')?.valueChanges.subscribe(value => {
      if (this.namingMode === 'custom') {
        if (this.mode === 'edit') {
          this.cachedCustomName = value;
        }
        this.updateDuplicateNameError(value);
      }
    });
  }

  initializeForm(): void {
    this.projectForm = this.fb.group({
      modelYear: ['', [Validators.required, Validators.pattern(/^\d{4}$/)]],
      modelCode: ['', [Validators.required]],
      projectCode: ['', [Validators.required]],
      projectName: ['', []]
    });
  }

  onTabChange(mode: 'standard' | 'custom'): void {
    if (this.mode === 'create') {
      this.projectForm.reset({
        modelYear: '',
        modelCode: '',
        projectCode: '',
        projectName: ''
      });
      this.generatedProjectName = '';
      this.duplicateNameError = '';
    }

    this.namingMode = mode;
    this.onNamingModeChange();
  }

  onNamingModeChange(): void {
    if (this.namingMode === 'standard') {
      this.projectForm.get('modelYear')?.enable();
      this.projectForm.get('modelCode')?.enable();
      this.projectForm.get('projectCode')?.enable();
      this.projectForm.get('modelYear')?.setValidators([Validators.required, Validators.pattern(/^\d{4}$/)]);
      this.projectForm.get('modelCode')?.setValidators([Validators.required]);
      this.projectForm.get('projectCode')?.setValidators([Validators.required]);
      this.projectForm.get('projectName')?.clearValidators();
      this.projectForm.get('projectName')?.disable();
      this.generateProjectName();
    } else {
      this.projectForm.get('modelYear')?.clearValidators();
      this.projectForm.get('modelCode')?.clearValidators();
      this.projectForm.get('projectCode')?.clearValidators();
      this.projectForm.get('projectName')?.enable();
      this.projectForm.get('projectName')?.setValidators([Validators.required]);
    }

    this.projectForm.get('modelYear')?.updateValueAndValidity();
    this.projectForm.get('modelCode')?.updateValueAndValidity();
    this.projectForm.get('projectCode')?.updateValueAndValidity();
    this.projectForm.get('projectName')?.updateValueAndValidity();

    if (this.namingMode === 'standard') {
      this.updateDuplicateNameError(this.generatedProjectName);
    } else {
      this.updateDuplicateNameError(this.projectForm.get('projectName')?.value);
    }
  }

  private toUpper(value: any): string {
    return (value || '').toString().trim().toUpperCase();
  }

  generateProjectName(): void {
    const modelYear = this.toUpper(this.projectForm.get('modelYear')?.value);
    const modelCode = this.toUpper(this.projectForm.get('modelCode')?.value);
    const projectCode = this.toUpper(this.projectForm.get('projectCode')?.value);

    const parts = [modelYear, modelCode, projectCode].filter(part => part !== '');

    this.generatedProjectName = parts.join('-');
    if(this.mode==='edit' && !this.generatedProjectName){
      return;
    }else{
      this.projectForm.patchValue({ projectName: this.generatedProjectName }, { emitEvent: false });
    }
    this.updateDuplicateNameError(this.generatedProjectName);
  }

  onFieldChange(): void {
    if (this.namingMode === 'standard') {
      const modelCodeControl = this.projectForm.get('modelCode');
      const projectCodeControl = this.projectForm.get('projectCode');

      const upperModelCode = this.toUpper(modelCodeControl?.value);
      if (modelCodeControl && modelCodeControl.value !== upperModelCode) {
        modelCodeControl.setValue(upperModelCode, { emitEvent: false });
      }

      const upperProjectCode = this.toUpper(projectCodeControl?.value);
      if (projectCodeControl && projectCodeControl.value !== upperProjectCode) {
        projectCodeControl.setValue(upperProjectCode, { emitEvent: false });
      }

      this.generateProjectName();
    }
  }

  selectColor(color: ColorOption): void {
    this.selectedColor = color;
    this.colorError = false;

    const existingProject = this.existingProjects.find(
      p => p.colorCode === color.code && !p.isDeleted
    );

    this.selectedColorProject = existingProject ? existingProject.projectName : '';
  }

  private populateForEdit(project: Project): void {
    const isStandard = project.namingMode === 'standard';

    if (isStandard) {
      this.projectForm.patchValue({
        modelYear: project.modelYear === 'N/A' ? '' : project.modelYear,
        modelCode: project.modelCode === 'N/A' ? '' : project.modelCode,
        projectCode: project.projectCode === 'N/A' ? '' : project.projectCode,
        projectName: project.projectName
      }, { emitEvent: false });
      this.generatedProjectName = project.projectName;
    } else {
      this.projectForm.patchValue({
        modelYear: '',
        modelCode: '',
        projectCode: '',
        projectName: project.projectName
      }, { emitEvent: false });
    }

    const matchedColor = this.colorOptions.find(c => c.code === project.colorCode || c.name === project.colorName);
    if (matchedColor) {
      this.selectedColor = matchedColor;
      this.colorError = false;
    }
  }

  private updateDuplicateNameError(name: string): void {
    const trimmed = (name || '').toString().trim();
    if (!trimmed) {
      this.duplicateNameError = '';
      return;
    }

    const normalizedNewName = trimmed.toLowerCase();
    const currentId = this.mode === 'edit' && this.editProjectData ? this.editProjectData.id : undefined;

    const isDuplicate = this.existingProjects.some(p =>
      !p.isDeleted &&
      p.id !== currentId &&
      p.projectName.trim().toLowerCase() === normalizedNewName
    );

    this.duplicateNameError = isDuplicate
      ? 'A project with this name already exists. Please use a different name.'
      : '';
  }

   submitProject(): void {
  if (!this.selectedColor) {
    this.colorError = true;
    return;
  }

  const isFormValid =
    this.projectForm.valid ||
    (this.namingMode === 'custom' &&
      this.projectForm.get('projectName')?.valid);

  if (!isFormValid) {
    alert('Please fill all required fields');
    return;
  }

  const formValues = this.projectForm.getRawValue();
  const base = this.mode === 'edit' && this.editProjectData
    ? this.editProjectData
    : undefined;

  const isStandard = this.namingMode === 'standard';

  const projectData: Project = {
    id: base ? base.id : Date.now().toString(),

    modelYear: isStandard
      ? (formValues.modelYear || 'N/A')
      : (base?.modelYear || 'N/A'),

    modelCode: isStandard
      ? (formValues.modelCode || 'N/A')
      : (base?.modelCode || 'N/A'),

    projectCode: isStandard
      ? (formValues.projectCode || 'N/A')
      : (base?.projectCode || 'N/A'),

    projectName: isStandard
      ? (this.generatedProjectName || formValues.projectName)
      : formValues.projectName,

    colorCode: this.selectedColor.code,
    colorName: this.selectedColor.name,
    namingMode: this.namingMode,

    dateCreated: base ? base.dateCreated : new Date().toISOString(),
    isDeleted: base ? base.isDeleted : false
  };

  this.updateDuplicateNameError(projectData.projectName);

  if (this.duplicateNameError) {
    return;
  }

  this.dialogRef.close(projectData);
}
}
